import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const DEFAULT_TAG_COLOR = '#6B7280';

const normalizeTagName = value =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeTagOption = tag => {
  const name = normalizeTagName(tag?.name || tag?.label || tag?.value || tag);
  const id = tag?.id ?? name;
  return {
    id,
    value: name,
    label: name,
    name,
    color: tag?.color || DEFAULT_TAG_COLOR,
  };
};

const getTagKey = tag =>
  normalizeTagName(tag?.name || tag?.label || tag?.value || tag).toLowerCase();

const TagCellPicker = ({
  options,
  selectedValues,
  onChange,
  readOnly,
  onTagsChanged,
}) => {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const normalizedOptions = useMemo(
    () =>
      (options || [])
        .map(normalizeTagOption)
        .filter(option => Boolean(option.name)),
    [options]
  );

  const normalizedSelection = useMemo(() => {
    const optionsById = new Map(
      normalizedOptions.map(option => [String(option.id), option])
    );
    return (selectedValues || [])
      .map(normalizeTagOption)
      .map(tag => optionsById.get(String(tag.id)) || tag)
      .filter(option => Boolean(option.name));
  }, [selectedValues, normalizedOptions]);

  const selectedKeys = useMemo(
    () => new Set(normalizedSelection.map(getTagKey)),
    [normalizedSelection]
  );

  const queryText = normalizeTagName(query);
  const filteredOptions = normalizedOptions.filter(option =>
    option.name.toLowerCase().includes(queryText.toLowerCase())
  );
  const matchingOption = normalizedOptions.find(
    option => option.name.toLowerCase() === queryText.toLowerCase()
  );
  const canCreate = Boolean(queryText) && !matchingOption;

  useEffect(() => {
    const handlePointerDown = event => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const emitChange = nextSelection => {
    onChange(nextSelection);
    window.setTimeout(() => onTagsChanged?.(), 800);
  };

  const toggleTag = tag => {
    if (readOnly) return;
    const key = getTagKey(tag);
    const alreadySelected = selectedKeys.has(key);
    const nextSelection = alreadySelected
      ? normalizedSelection.filter(item => getTagKey(item) !== key)
      : [...normalizedSelection, tag];
    emitChange(nextSelection);
    setQuery('');
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleCreateTag = () => {
    if (!queryText || readOnly) return;
    const nextTag = matchingOption || {
      id: `new-${queryText.toLowerCase()}`,
      value: queryText,
      label: queryText,
      name: queryText,
      color: DEFAULT_TAG_COLOR,
    };
    if (!selectedKeys.has(getTagKey(nextTag))) {
      emitChange([...normalizedSelection, nextTag]);
    }
    setQuery('');
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = event => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      if (matchingOption) {
        toggleTag(matchingOption);
      } else {
        handleCreateTag();
      }
      return;
    }
    if (event.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
      inputRef.current?.blur();
      return;
    }
    if (event.key === 'Backspace' && !query && normalizedSelection.length > 0) {
      event.preventDefault();
      emitChange(normalizedSelection.slice(0, -1));
    }
  };

  return (
    <div
      className="tag-cell-picker"
      ref={rootRef}
      onMouseDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}
    >
      <div
        className="tag-cell-picker__surface"
        aria-disabled={readOnly}
        onClick={() => {
          if (readOnly) return;
          setIsOpen(true);
          window.setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        <span className="tag-cell-picker__chips">
          {normalizedSelection.length > 0 &&
            normalizedSelection.map(tag => (
              <span
                key={getTagKey(tag)}
                className="tag-chip"
                style={{ '--tag-color': tag.color || DEFAULT_TAG_COLOR }}
              >
                <span className="tag-chip__label">{tag.name}</span>
                {!readOnly && (
                  <span
                    role="button"
                    tabIndex={0}
                    className="tag-chip__remove"
                    aria-label={`Remove ${tag.name}`}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      toggleTag(tag);
                    }}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        toggleTag(tag);
                      }
                    }}
                  >
                    &times;
                  </span>
                )}
              </span>
            ))}
          {readOnly && !normalizedSelection.length && !query && (
            <span className="tag-cell-picker__placeholder">Tags</span>
          )}
          {!readOnly && (
            <input
              ref={inputRef}
              className="tag-cell-picker__input"
              value={query}
              onChange={event => {
                setQuery(event.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              onKeyDown={handleKeyDown}
              placeholder={normalizedSelection.length ? '' : 'Type tag'}
              autoComplete="off"
            />
          )}
        </span>
      </div>

      {isOpen && !readOnly && (
        <div className="tag-cell-picker__menu">
          <div className="tag-cell-picker__options">
            {filteredOptions.map(option => {
              const selected = selectedKeys.has(getTagKey(option));
              return (
                <button
                  key={getTagKey(option)}
                  type="button"
                  className={selected ? 'is-selected' : ''}
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => toggleTag(option)}
                >
                  <span
                    className="tag-cell-picker__swatch"
                    style={{
                      backgroundColor: option.color || DEFAULT_TAG_COLOR,
                    }}
                  />
                  <span>{option.name}</span>
                  {selected && (
                    <span className="tag-cell-picker__check">Selected</span>
                  )}
                </button>
              );
            })}
            {canCreate && (
              <button
                type="button"
                onMouseDown={event => event.preventDefault()}
                onClick={handleCreateTag}
              >
                <span
                  className="tag-cell-picker__swatch"
                  style={{ backgroundColor: DEFAULT_TAG_COLOR }}
                />
                <span>Create &quot;{queryText}&quot;</span>
              </button>
            )}
            {!filteredOptions.length && !canCreate && (
              <div className="tag-cell-picker__empty">No tags</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

TagCellPicker.propTypes = {
  options: PropTypes.array,
  selectedValues: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  onTagsChanged: PropTypes.func,
};

TagCellPicker.defaultProps = {
  options: [],
  selectedValues: [],
  readOnly: false,
  onTagsChanged: () => null,
};

export default TagCellPicker;
